import { Button, Form, Input, Modal, QRCode, Spin, Typography, message } from 'antd';
import { MobileOutlined, QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import requestOtp from '../../../api/bank-account/requestOtp';
import submitOtp from '../../../api/bank-account/submitOtp';
import useAuthHeader from '../../../hooks/useAuthHeader';
import {
    getBankAccountAuth,
    isAuthLoginComplete,
    isOtpReAuthSuccess,
    isOtpRequestComplete,
    isPushPending,
    shouldContinuePushWait,
} from '../../../helpers/bankAccountAuth';
import styles from '../BankAccountAuthModal/BankAccountAuthModal.module.scss';

const PUSH_POLL_INTERVAL_MS = 1500;
const PUSH_WAIT_MS = 2 * 60 * 1000;

function BankAccountOtpModal({
    open,
    uuid,
    initialMessage,
    initialOtpMode = null,
    initialQrCode = '',
    onCancel,
    onSuccess,
}) {
    const accessToken = useAuthHeader();
    const [form] = Form.useForm();
    const [otpMode, setOtpMode] = useState(null);
    const [qrCode, setQrCode] = useState('');
    const [apiMessage, setApiMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [pushWaiting, setPushWaiting] = useState(false);
    const [pushSecondsLeft, setPushSecondsLeft] = useState(0);

    const pollTimerRef = useRef(null);
    const pushDeadlineRef = useRef(0);
    const countdownTimerRef = useRef(null);
    const pushStartedRef = useRef(false);
    const autoPushStartedRef = useRef(false);

    const clearPollTimer = useCallback(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const clearCountdownTimer = useCallback(() => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    }, []);

    const startPushCountdown = useCallback(() => {
        pushDeadlineRef.current = Date.now() + PUSH_WAIT_MS;
        clearCountdownTimer();
        const tick = () => {
            const left = Math.max(0, Math.ceil((pushDeadlineRef.current - Date.now()) / 1000));
            setPushSecondsLeft(left);
            if (left <= 0) {
                clearCountdownTimer();
                setPushWaiting(false);
                setApiMessage('Hết thời gian chờ xác nhận trên app (2 phút).');
            }
        };
        tick();
        countdownTimerRef.current = setInterval(tick, 1000);
    }, [clearCountdownTimer]);

    const applyChallengeState = useCallback((response) => {
        const auth = getBankAccountAuth(response);
        if (auth.authMessage || auth.message) {
            setApiMessage(auth.authMessage || auth.message);
        }
        if (auth.otpMode) {
            setOtpMode(auth.otpMode);
        }
        if (auth.qrCode) {
            setQrCode(auth.qrCode);
        }
        return auth;
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }
        form.resetFields();
        setOtpMode(initialOtpMode);
        setQrCode(initialQrCode);
        setApiMessage(initialMessage || '');
        setLoading(false);
        setPushWaiting(false);
        setPushSecondsLeft(0);
        pushStartedRef.current = false;
        autoPushStartedRef.current = false;
        pushDeadlineRef.current = 0;
        clearPollTimer();
        clearCountdownTimer();
    }, [open, uuid, initialMessage, initialOtpMode, initialQrCode, form, clearPollTimer, clearCountdownTimer]);

    useEffect(() => () => {
        clearPollTimer();
        clearCountdownTimer();
    }, [clearCountdownTimer, clearPollTimer]);

    const applyResponse = (response, { fromRequest = false } = {}) => {
        const auth = applyChallengeState(response);
        const completed = fromRequest
            ? isOtpRequestComplete(response)
            : isOtpReAuthSuccess(response);

        if (completed || isAuthLoginComplete(response)) {
            message.success(auth.message || auth.authMessage || 'Xác thực thành công');
            onSuccess?.();
            return { done: true, auth };
        }

        if (fromRequest && auth.otpMode) {
            setOtpMode(auth.otpMode);
        }

        return { done: false, auth, continuePush: shouldContinuePushWait(response) };
    };

    const submitPushConfirm = useCallback(async (auto = false) => {
        if (!uuid) {
            return { done: false };
        }
        if (pushDeadlineRef.current && Date.now() >= pushDeadlineRef.current) {
            if (auto) {
                setPushWaiting(false);
                setApiMessage('Hết thời gian chờ xác nhận trên app (2 phút).');
                return { done: false };
            }
            startPushCountdown();
        } else if (!pushDeadlineRef.current) {
            startPushCountdown();
        }

        setPushWaiting(true);
        setLoading(true);
        const response = await submitOtp(accessToken, uuid, { uuid, otp: '' });
        setLoading(false);

        if (isAuthLoginComplete(response)) {
            applyResponse(response);
            return { done: true, response };
        }

        const { continuePush } = applyResponse(response);
        if (continuePush || shouldContinuePushWait(response) || isPushPending(response)) {
            setPushWaiting(true);
            clearPollTimer();
            pollTimerRef.current = setTimeout(() => {
                submitPushConfirm(true);
            }, PUSH_POLL_INTERVAL_MS);
            return { done: false, response, polling: true };
        }

        setPushWaiting(false);
        if (!auto) {
            message.error(getBankAccountAuth(response).authMessage || response.message || 'Xác thực thất bại');
        }
        return { done: false, response };
    }, [accessToken, applyResponse, clearPollTimer, startPushCountdown, uuid]);

    const beginPushFlow = useCallback(() => {
        if (pushStartedRef.current) {
            return;
        }
        pushStartedRef.current = true;
        startPushCountdown();
        setPushWaiting(true);
        clearPollTimer();
        pollTimerRef.current = setTimeout(() => {
            submitPushConfirm(true);
        }, PUSH_POLL_INTERVAL_MS);
    }, [clearPollTimer, startPushCountdown, submitPushConfirm]);

    useEffect(() => {
        if (!open || otpMode !== 'push') {
            return;
        }

        if (qrCode) {
            beginPushFlow();
            return;
        }

        if (initialOtpMode === 'push') {
            if (autoPushStartedRef.current) {
                return;
            }
            autoPushStartedRef.current = true;

            (async () => {
                setLoading(true);
                const response = await requestOtp(accessToken, uuid);
                setLoading(false);
                if (response.success) {
                    const result = applyResponse(response, { fromRequest: true });
                    if (!result.done) {
                        beginPushFlow();
                    }
                } else {
                    const auth = getBankAccountAuth(response);
                    setApiMessage(auth.message || response.message || 'Gửi yêu cầu xác thực thất bại');
                }
            })();
            return;
        }

        beginPushFlow();
    }, [open, otpMode, qrCode, initialOtpMode, uuid, accessToken, beginPushFlow]);

    const handleRequestOtp = async () => {
        if (!uuid) {
            return;
        }
        setLoading(true);
        const response = await requestOtp(accessToken, uuid);
        if (!response.success) {
            const auth = getBankAccountAuth(response);
            setApiMessage(auth.message || response.message || 'Gửi yêu cầu xác thực thất bại');
            message.error(auth.message || response.message || 'Gửi yêu cầu xác thực thất bại');
        } else {
            const result = applyResponse(response, { fromRequest: true });
            if (!result.done && result.continuePush && result.auth?.otpMode === 'push') {
                beginPushFlow();
            }
        }
        setLoading(false);
    };

    const handleSubmitOtp = async (otpValue = '') => {
        if (!uuid) {
            return;
        }

        if (otpMode === 'push') {
            await submitPushConfirm(false);
            return;
        }

        setLoading(true);
        const response = await submitOtp(accessToken, uuid, {
            uuid,
            otp: otpValue,
        });
        if (isOtpReAuthSuccess(response) || isAuthLoginComplete(response)) {
            applyResponse(response);
        } else {
            const auth = getBankAccountAuth(response);
            setApiMessage(auth.authMessage || auth.message || response.message || 'Xác thực thất bại');
            message.error(auth.authMessage || auth.message || response.message || 'Xác thực thất bại');
            if (auth.otpMode) {
                setOtpMode(auth.otpMode);
            }
            if (auth.qrCode) {
                setQrCode(auth.qrCode);
            }
            if (auth.otpMode === 'push') {
                pushStartedRef.current = false;
                beginPushFlow();
            }
        }
        setLoading(false);
    };

    const handleFinish = (values) => {
        handleSubmitOtp(values?.otp || '');
    };

    const requested = Boolean(otpMode);
    const isPush = otpMode === 'push';
    const showOtpInput = requested && !isPush;

    const footer = [
        <Button key="cancel" onClick={onCancel}>
            Hủy
        </Button>,
    ];

    if (!requested) {
        footer.push(
            <Button key="request" type="primary" loading={loading} onClick={handleRequestOtp}>
                Gửi yêu cầu xác thực
            </Button>
        );
    } else if (isPush) {
        footer.push(
            <Button key="push" type="primary" loading={loading} icon={<ReloadOutlined />} onClick={() => handleSubmitOtp('')}>
                Tôi đã xác nhận
            </Button>
        );
    } else {
        footer.push(
            <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
                Xác nhận
            </Button>
        );
    }

    return (
        <Modal
            title="Xác thực tài khoản ngân hàng"
            open={open}
            onCancel={onCancel}
            footer={footer}
            destroyOnClose
            maskClosable={!loading}
            width={480}
        >
            <Typography.Paragraph style={{ marginTop: 8 }}>
                {apiMessage || 'Tài khoản đang chờ xác thực. Gửi yêu cầu để nhận OTP hoặc thông báo trên ứng dụng ngân hàng.'}
            </Typography.Paragraph>

            {isPush && (
                <div className={styles.pushPanel}>
                    <div className={styles.pushBadge}>
                        {qrCode ? <QrcodeOutlined /> : <MobileOutlined />}
                        {qrCode ? 'Quét mã QR trên app' : 'Xác nhận trên app ngân hàng'}
                    </div>
                    {qrCode ? (
                        <div className={styles.qrWrap}>
                            <QRCode value={qrCode} size={200} bordered={false} />
                        </div>
                    ) : null}
                    <div className={styles.waitingRow}>
                        {pushWaiting || loading ? <Spin size="small" /> : null}
                        <span>
                            {loading && !pushWaiting
                                ? 'Đang gửi yêu cầu xác thực...'
                                : pushWaiting
                                    ? `Đang tự động kiểm tra xác nhận${pushSecondsLeft > 0 ? ` (${pushSecondsLeft}s)` : ''}...`
                                    : 'Mở app ngân hàng và xác nhận đăng nhập'}
                        </span>
                    </div>
                </div>
            )}

            {showOtpInput && (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                >
                    <Form.Item
                        name="otp"
                        label="Mã OTP"
                        rules={[{ required: true, message: 'Vui lòng nhập mã OTP' }]}
                    >
                        <Input placeholder="Nhập mã OTP" autoComplete="one-time-code" inputMode="numeric" />
                    </Form.Item>
                </Form>
            )}
        </Modal>
    );
}

export default BankAccountOtpModal;
